import React, {useState, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import {RootState} from '../store';

import {text} from '../text';
import {hooks} from '../hooks';
import {utils} from '../utils';
import {custom} from '../custom';
import {theme} from '../constants';
import {components} from '../components';
import {svg} from '../assets/svg';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { compressImage } from '../utils/imageUtils';
import { MdPersonOutline, MdDescription, MdBusinessCenter, MdDirectionsBoat, MdKeyboardArrowDown, MdCheckCircle, MdError, MdWarning, MdCheckBox, MdCheckBoxOutlineBlank, MdCalendarMonth, MdAttachFile, MdDelete, MdSearch, MdClose, MdChevronLeft, MdChevronRight } from 'react-icons/md';

// Fix for TS2786: 'Icon' cannot be used as a JSX component.
const PersonIcon = MdPersonOutline as any;
const DescriptionIcon = MdDescription as any;
const BusinessIcon = MdBusinessCenter as any;
const BoatIcon = MdDirectionsBoat as any;
const ArrowIcon = MdKeyboardArrowDown as any;
const CheckCircleIcon = MdCheckCircle as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;
const CheckBoxIcon = MdCheckBox as any;
const CheckBoxOutlineBlankIcon = MdCheckBoxOutlineBlank as any;
const CalendarIcon = MdCalendarMonth as any;
const AttachFileIcon = MdAttachFile as any;
const DeleteIcon = MdDelete as any;
const SearchIcon = MdSearch as any;
const CloseIcon = MdClose as any;
const ChevronLeftIcon = MdChevronLeft as any;
const ChevronRightIcon = MdChevronRight as any;

export const CreateTask: React.FC = () => {
  const {pathname, state} = useLocation();
  const parentTask = state?.parentTask;
  const navigate = hooks.useAppNavigate();
  const {authSlice} = hooks.useAppSelector((state: RootState) => state);
  const { t, language } = useLanguage();

  const [users, setUsers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [vessels, setVessels] = useState<any[]>([]);
  const [selectedChecker, setSelectedChecker] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<string>(parentTask?.client_id?.toString() || '');
  const [selectedVessel, setSelectedVessel] = useState<string>(parentTask?.vessel_id?.toString() || '');
  const [vesselSummary, setVesselSummary] = useState<any>(null);
  const [selectedVesselTask, setSelectedVesselTask] = useState<any>(null);
  const [sheetDragDelta, setSheetDragDelta] = useState<number>(0);
  const [sheetIsClosing, setSheetIsClosing] = useState<boolean>(false);
  const sheetDragStartY = useRef<number>(0);
  const sheetDragActive = useRef<boolean>(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  const closeSheet = () => {
    const height = sheetRef.current?.offsetHeight ?? 500;
    setSheetIsClosing(true);
    setSheetDragDelta(height);
    setTimeout(() => {
      setSelectedVesselTask(null);
      setSheetDragDelta(0);
      setSheetIsClosing(false);
    }, 260);
  };
  const [lightbox, setLightbox] = useState<{ attachments: any[]; idx: number } | null>(null);
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || '';
  const [taskType, setTaskType] = useState<string>(parentTask?.task_type || '');

  const [description, setDescription] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [taskTypes, setTaskTypes] = useState<any[]>([]);
  const [availableAttachments, setAvailableAttachments] = useState<any[]>([]);
  const [selectedAttachments, setSelectedAttachments] = useState<number[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [isUrgent, setIsUrgent] = useState<boolean>(false);
  const [isMaintenance, setIsMaintenance] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [searchChecklist, setSearchChecklist] = useState<string>('');
  const [isDurationManual, setIsDurationManual] = useState<boolean>(false);
  const [manualDurationHours, setManualDurationHours] = useState<string>('');
  const [manualDurationMins, setManualDurationMins] = useState<string>('');
  const [manualStartDate, setManualStartDate] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const processedFiles = await Promise.all(newFiles.map(async (file) => {
        if (file.type.startsWith('image/')) {
          try { return await compressImage(file); } catch (error) { return file; }
        }
        return file;
      }));
      setAttachments(prev => [...prev, ...processedFiles]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning';
    onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  const timeAgo = (dateStr: string): string => {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    const days = Math.floor(diff / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  const formatShortDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  useEffect(() => {
    setTimeout(() => {
      window.scroll({top: -1, left: 0, behavior: 'smooth'});
    }, 10);
  }, [pathname]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get('/users');
        setUsers(response.data);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };
    fetchUsers();
  }, []);

  // Fetch Clients
  useEffect(() => {
    const fetchTaskTypes = async () => {
      try {
        const response = await axiosInstance.get('/settings/task-types');
        setTaskTypes(response.data);
      } catch (error) {
        console.error('Failed to fetch task types:', error);
      }
    };
    fetchTaskTypes();
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axiosInstance.get('/clients');
        setClients(response.data);
      } catch (error) {
        console.error('Failed to fetch clients:', error);
        showAlert(t.common.error, t.createTask.alerts.fetchClientsFailed, 'error');
      }
    };
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch Vessels when Client changes
  useEffect(() => {
    if (selectedClient) {
      const fetchVessels = async () => {
        try {
          const response = await axiosInstance.get(`/vessels/client/${selectedClient}`);
          setVessels(response.data);
        } catch (error) {
          console.error('Failed to fetch vessels:', error);
          showAlert(t.common.error, t.createTask.alerts.fetchVesselsFailed, 'error');
        }
      };
      fetchVessels();
    } else {
      setVessels([]);
      setSelectedVessel('');
      setVesselSummary(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  // Fetch vessel task summary when vessel changes
  useEffect(() => {
    if (selectedVessel) {
      axiosInstance.get(`/vessels/${selectedVessel}/task-summary`)
        .then(r => setVesselSummary(r.data))
        .catch(() => setVesselSummary(null));
    } else {
      setVesselSummary(null);
    }
  }, [selectedVessel]);

  // Fetch Attachment Types when Task Type changes
  useEffect(() => {
    if (taskType) {
      const selectedTypeObj = taskTypes.find((t: any) => t.type === taskType);
      if (selectedTypeObj) {
        const fetchAttachments = async () => {
          try {
            const response = await axiosInstance.get(`/task-checklists?task_type_id=${selectedTypeObj.id}`);
            setAvailableAttachments(response.data.filter((c: any) => c.is_active !== false));
            // Default: select all
            setSelectedAttachments([]);
            setSelectedGroups([]);
            setSearchChecklist('');
          } catch (error) {
            console.error('Failed to fetch attachment types:', error);
          }
        };
        fetchAttachments();
      }
    }
  }, [taskType, taskTypes]);

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void) => {
    setAlertConfig({
      isOpen: true,
      title,
      message,
      type,
      onClose,
    });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
    if (onClose) {
      onClose();
    }
  };

  const filteredAttachments = React.useMemo(() => {
    if (!searchChecklist.trim()) return availableAttachments;
    const lowerSearch = searchChecklist.toLowerCase();
    return availableAttachments.filter(att => 
      att.title?.toLowerCase().includes(lowerSearch) || 
      att.group_name?.toLowerCase().includes(lowerSearch)
    );
  }, [availableAttachments, searchChecklist]);

  const groupedAttachments = React.useMemo(() => {
    const groups: { [key: string]: any[] } = {};
    const othersLabel = t.common?.others || 'Lainnya';

    filteredAttachments.forEach(att => {
      const groupName = att.group_name || othersLabel;
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(att);
    });
    
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === othersLabel) return 1; // Pindah ke paling bawah
      if (b === othersLabel) return -1; // Pindah ke paling bawah
      return a.localeCompare(b);
    });

    return sortedKeys.map(key => ({
      group_name: key,
      attachments: groups[key]
    }));
  }, [filteredAttachments, t.common?.others]);

  const handleCheckAll = () => {
    const idsToAdd = filteredAttachments.map(a => a.id);
    setSelectedAttachments(prev => Array.from(new Set([...prev, ...idsToAdd])));
    const groupsToAdd = groupedAttachments.map(g => g.group_name);
    setSelectedGroups(prev => Array.from(new Set([...prev, ...groupsToAdd])));
    setOpenGroups(prev => Array.from(new Set([...prev, ...groupsToAdd])));
  };

  const handleUncheckAll = () => {
    const idsToRemove = filteredAttachments.map(a => a.id);
    setSelectedAttachments(prev => prev.filter(id => !idsToRemove.includes(id)));
    const groupsToRemove = groupedAttachments.map(g => g.group_name);
    setSelectedGroups(prev => prev.filter(g => !groupsToRemove.includes(g)));
  };

  const toggleGroupOpen = (groupName: string) => {
    setOpenGroups(prev =>
      prev.includes(groupName) ? prev.filter(g => g !== groupName) : [...prev, groupName]
    );
  };

  const toggleGroup = (groupName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedGroups.includes(groupName)) {
      setSelectedGroups(prev => prev.filter(g => g !== groupName));
      const attachmentsInGroup = groupedAttachments.find(g => g.group_name === groupName)?.attachments || [];
      const idsToRemove = attachmentsInGroup.map(a => a.id);
      setSelectedAttachments(prev => prev.filter(id => !idsToRemove.includes(id)));
    } else {
      setSelectedGroups(prev => [...prev, groupName]);
      const attachmentsInGroup = groupedAttachments.find(g => g.group_name === groupName)?.attachments || [];
      const idsToAdd = attachmentsInGroup.map(a => a.id);
      setSelectedAttachments(prev => Array.from(new Set([...prev, ...idsToAdd])));
      setOpenGroups(prev => prev.includes(groupName) ? prev : [...prev, groupName]);
    }
  };

  const toggleAttachment = (id: number) => {
    if (selectedAttachments.includes(id)) {
      setSelectedAttachments(prev => prev.filter(item => item !== id));
    } else {
      setSelectedAttachments(prev => [...prev, id]);
    }
  };

  const totalDuration = React.useMemo(() => {
    const totalMins = availableAttachments
      .filter(att => selectedAttachments.includes(att.id))
      .reduce((sum, att) => sum + (Number(att.duration) || 0), 0);
    const days = Math.floor(totalMins / 1440);
    const hours = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;
    const parts = [];
    if (days > 0) parts.push(`${days} ${language === 'id' ? 'Hari' : 'Days'}`);
    if (hours > 0) parts.push(`${hours} ${language === 'id' ? 'Jam' : 'Hours'}`);
    if (mins > 0) parts.push(`${mins} ${language === 'id' ? 'Menit' : 'Minutes'}`);
    return parts.length > 0 ? parts.join(' ') : (language === 'id' ? '0 Menit' : '0 Minutes');
  }, [availableAttachments, selectedAttachments, language]);

  const handleCreateTask = async () => {
    const currentUserId = authSlice.userId;

    if (!currentUserId) {
      showAlert(t.common.error, t.createTask.alerts.missingUserId, 'error');
      return;
    }

    if (!taskType || !description) {
      showAlert(t.common.warning, t.createTask.alerts.fillRequired, 'warning');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('assigned_by_user_id', currentUserId.toString());
      if (selectedUser) formData.append('assigned_to_user_id', selectedUser);
      formData.append('task_type', taskType);
      formData.append('description', description);
      if (selectedClient) formData.append('client_id', selectedClient);
      if (selectedVessel) formData.append('vessel_id', selectedVessel);
      formData.append('is_urgent', isUrgent.toString());
      formData.append('is_maintenance', isMaintenance.toString());
      formData.append('duration_is_manual', isDurationManual.toString());
      if (isDurationManual) {
        const totalMins = (parseInt(manualDurationHours || '0', 10) * 60) + parseInt(manualDurationMins || '0', 10);
        formData.append('manual_duration', totalMins.toString());
        if (manualStartDate) formData.append('start_date', new Date(manualStartDate).toISOString());
      }
      if (parentTask?.id) formData.append('parent_task_id', parentTask.id.toString());
      if (selectedChecker) formData.append('checker_user_id', selectedChecker);
      
      selectedAttachments.forEach(id => {
        formData.append('task_checklist_ids', id.toString());
      });

      attachments.forEach(file => {
        formData.append('attachments', file);
      });

      const response = await axiosInstance.post('/tasks', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const createdTask = response.data;
      showAlert(t.common.success, t.createTask.alerts.success, 'success', () => {
        navigate('/TaskDetails', { state: { task: createdTask }, replace: true });
      });
    } catch (error) {
      console.error('Failed to create task:', error);
      showAlert(t.common.error, t.createTask.alerts.failed, 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderBackground = (): JSX.Element => {
    return <components.Background />;
  };

  const renderHeader = (): JSX.Element => {
    return (
      <components.Header
        title={t.createTask.title}
        goBack={true}
      />
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <main
        style={{marginTop: 62, paddingBottom: 40}}
        className='container'
      >

        {parentTask && (
          <div style={{ padding: 12, backgroundColor: '#EFF6FF', borderRadius: 10, border: '1px solid #BFDBFE', marginBottom: 16 }}>
            <text.T14 style={{ color: '#1D4ED8', fontWeight: 'bold' }}>
              {t.createTask.followUpFor || 'Tugas Lanjutan untuk'}:
            </text.T14>
            <text.T14 style={{ color: '#1E3A8A', marginTop: 4 }} numberOfLines={2}>
              {parentTask.description}
            </text.T14>
          </div>
        )}

        {/* Select Task Type */}
        <div style={{marginBottom: 10, position: 'relative'}}>
          <div
            style={{
              position: 'absolute',
              paddingLeft: 10,
              paddingTop: 15,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <DescriptionIcon size={20} color={theme.colors.mainDark} />
          </div>
          <select
            value={taskType}
            onChange={(e) => setTaskType(e.target.value)}
            style={{
              width: '100%',
              height: 50,
              paddingLeft: 50,
              paddingRight: 20,
              borderRadius: 10,
              border: '1px solid #FFEFE6',
              backgroundColor: theme.colors.white,
              appearance: 'none',
              fontSize: 16,
              color: theme.colors.mainDark,
              ...theme.fonts.SourceSansPro_400Regular,
            }}
          >
            <option value='' disabled hidden>{t.form.chooseTaskType}</option>
            {taskTypes.map((item, index) => (
              <option key={index} value={item.type}>{item.type}</option>
            ))}
          </select>
          <div style={{position: 'absolute', right: 20, top: 15, zIndex: 1, pointerEvents: 'none'}}>
            <ArrowIcon size={20} color={theme.colors.mainDark} />
          </div>
        </div>

        {/* Select Checker */}
        <div style={{marginBottom: 10}}>
          <components.SearchableDropdown
            options={users}
            value={selectedChecker}
            onChange={setSelectedChecker}
            placeholder={(t.createTask as any).checker || 'Checker (Optional)'}
            searchPlaceholder={(t.createTask as any).chooseChecker || 'Cari Pemeriksa...'}
            leftIcon={<PersonIcon size={20} color={theme.colors.mainDark} />}
          />
        </div>

        {/* Urgent Checkbox */}
        <div 
          onClick={() => setIsUrgent(!isUrgent)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, 
            padding: '6px 10px', backgroundColor: isUrgent ? '#FEF2F2' : '#FFF', borderRadius: 8,
            border: isUrgent ? `1px solid #EF4444` : '1px solid #FFEFE6',
            cursor: 'pointer', marginBottom: 10
          }}
        >
          {isUrgent ? <CheckBoxIcon size={20} color="#EF4444" /> : <CheckBoxOutlineBlankIcon size={20} color="#9CA3AF" />}
          <text.T14 style={{color: isUrgent ? '#EF4444' : theme.colors.mainDark, fontWeight: isUrgent ? '600' : 'normal'}}>{t.createTask.isUrgent}</text.T14>
        </div>

        {/* Maintenance Checkbox */}
        <div 
          onClick={() => setIsMaintenance(!isMaintenance)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, 
            padding: '6px 10px', backgroundColor: isMaintenance ? '#EFF6FF' : '#FFF', borderRadius: 8,
            border: isMaintenance ? `1px solid #3B82F6` : '1px solid #FFEFE6',
            cursor: 'pointer', marginBottom: 10
          }}
        >
          {isMaintenance ? <CheckBoxIcon size={20} color="#3B82F6" /> : <CheckBoxOutlineBlankIcon size={20} color="#9CA3AF" />}
          <text.T14 style={{color: isMaintenance ? '#3B82F6' : theme.colors.mainDark, fontWeight: isMaintenance ? '600' : 'normal'}}>{(t.createTask as any).isMaintenance || 'Tandai sebagai Maintenance'}</text.T14>
        </div>

        {/* Conditional Inputs based on task type flags */}
        {taskTypes.find((tt: any) => tt.type === taskType)?.show_client && (
          <components.SearchableDropdown
            options={clients}
            value={selectedClient}
            onChange={setSelectedClient}
            placeholder={t.form.chooseClient}
            searchPlaceholder={t.form.searchClient}
            leftIcon={<BusinessIcon size={20} color={theme.colors.mainDark} />}
          />
        )}
        {taskTypes.find((tt: any) => tt.type === taskType)?.show_vessel && (
          <components.SearchableDropdown
            options={vessels}
            value={selectedVessel}
            onChange={setSelectedVessel}
            placeholder={t.form.chooseVessel || 'Pilih Kapal'}
            searchPlaceholder={t.form.searchVessel || 'Cari kapal...'}
            leftIcon={<BoatIcon size={20} color={theme.colors.mainDark} />}
            disabled={!selectedClient}
          />
        )}

        {/* Vessel task history summary */}
        {vesselSummary && selectedVessel && (
          <div style={{
            marginBottom: 12,
            borderRadius: 10,
            border: `1px solid ${vesselSummary.open_tasks_count > 0 ? '#FCA5A5' : '#E5E7EB'}`,
            backgroundColor: vesselSummary.open_tasks_count > 0 ? '#FFF5F5' : '#F9FAFB',
            overflow: 'hidden',
          }}>
            {/* open tasks warning */}
            {vesselSummary.open_tasks_count > 0 && (
              <div style={{
                padding: '6px 12px',
                backgroundColor: '#FEE2E2',
                borderBottom: '1px solid #FCA5A5',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <WarningIcon size={14} color="#DC2626" />
                <text.T12 style={{ color: '#DC2626', fontWeight: '600' }}>
                  {vesselSummary.open_tasks_count} open task{vesselSummary.open_tasks_count > 1 ? 's' : ''} on this vessel
                </text.T12>
              </div>
            )}
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* stats row */}
              <div style={{ display: 'flex', gap: 16 }}>
                <div>
                  <text.T10 style={{ color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>All time</text.T10>
                  <text.T14 style={{ color: theme.colors.mainDark, fontWeight: '600' }}>
                    {vesselSummary.total_completed}/{vesselSummary.total_tasks} completed
                  </text.T14>
                </div>
                {vesselSummary.avg_score != null && (
                  <div>
                    <text.T10 style={{ color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>Avg score</text.T10>
                    <text.T14 style={{ color: theme.colors.mainDark, fontWeight: '600' }}>{vesselSummary.avg_score}%</text.T14>
                  </div>
                )}
              </div>
              {/* recent tasks */}
              {vesselSummary.recent_tasks?.length > 0 && (
                <div style={{ paddingTop: 6, borderTop: '1px solid #E5E7EB' }}>
                  {vesselSummary.recent_tasks.map((task: any, idx: number) => (
                    <div key={task.id} style={{
                      paddingTop: idx === 0 ? 0 : 6,
                      marginTop: idx === 0 ? 0 : 6,
                      borderTop: idx === 0 ? 'none' : '1px dashed #E5E7EB',
                      display: 'flex', flexDirection: 'column', gap: 3,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <text.T12 style={{ color: theme.colors.mainDark, fontWeight: '600' }}>{task.status}</text.T12>
                          <text.T12 style={{ color: '#9CA3AF' }}>·</text.T12>
                          <text.T12 style={{ color: '#9CA3AF' }}>{formatShortDate(task.created_at)} · {timeAgo(task.created_at)}</text.T12>
                        </div>
                        <span
                          onClick={() => setSelectedVesselTask(task)}
                          style={{ flexShrink: 0, cursor: 'pointer' }}
                        >
                          <text.T10 style={{ color: theme.colors.mainColor || '#F97316' }}>details →</text.T10>
                        </span>
                      </div>
                      {task.description && (
                        <text.T12 style={{
                          color: '#6B7280',
                          overflow: 'hidden', display: '-webkit-box',
                          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          lineHeight: '1.4',
                        }}>
                          {task.description}
                        </text.T12>
                      )}
                      {task.assignee_names?.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <PersonIcon size={12} color="#9CA3AF" />
                          <text.T12 style={{ color: '#6B7280' }}>{task.assignee_names.join(', ')}</text.T12>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Vessel task detail modal */}
        {selectedVesselTask && (
          <div
            onClick={closeSheet}
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'flex-end',
            }}
          >
            <div
              ref={sheetRef}
              onClick={e => e.stopPropagation()}
              onTouchStart={e => {
                sheetDragStartY.current = e.touches[0].clientY;
                sheetDragActive.current = (sheetRef.current?.scrollTop ?? 0) === 0;
              }}
              onTouchMove={e => {
                if (!sheetDragActive.current) return;
                const delta = e.touches[0].clientY - sheetDragStartY.current;
                if (delta > 0) setSheetDragDelta(delta);
              }}
              onTouchEnd={() => {
                if (!sheetDragActive.current) return;
                sheetDragActive.current = false;
                if (sheetDragDelta > 80) {
                  closeSheet();
                } else {
                  setSheetDragDelta(0);
                }
              }}
              style={{
                width: '100%', backgroundColor: '#fff',
                borderRadius: '16px 16px 0 0',
                padding: '20px 20px 36px',
                maxHeight: '80vh', overflowY: 'auto',
                transform: `translateY(${sheetDragDelta}px)`,
                transition: (sheetDragDelta === 0 || sheetIsClosing) ? 'transform 0.26s ease' : 'none',
              }}
            >
              {/* Drag handle — visual only */}
              <div style={{ padding: '0 0 12px' }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: '#E5E7EB', margin: '0 auto' }} />
              </div>

              {(() => {
                const task = selectedVesselTask;
                const statusColors: Record<string, { bg: string; text: string }> = {
                  completed:  { bg: '#D1FAE5', text: '#065F46' },
                  rejected:   { bg: '#FEE2E2', text: '#991B1B' },
                  incomplete: { bg: '#FEF3C7', text: '#92400E' },
                };
                const sc = statusColors[task.status] ?? { bg: '#F3F4F6', text: '#374151' };
                return (
                  <>
                    {/* header: status chip + date */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <div style={{ paddingInline: 10, paddingBlock: 4, borderRadius: 20, backgroundColor: sc.bg }}>
                        <text.T12 style={{ fontWeight: '600', color: sc.text }}>{task.status}</text.T12>
                      </div>
                      <text.T12 style={{ color: '#9CA3AF' }}>{formatShortDate(task.created_at)} · {timeAgo(task.created_at)}</text.T12>
                    </div>

                    {task.task_type && (
                      <div style={{ marginBottom: 12 }}>
                        <text.T10 style={{ color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>Task Type</text.T10>
                        <text.T14 style={{ color: theme.colors.mainDark }}>{task.task_type}</text.T14>
                      </div>
                    )}

                    {task.description && (
                      <div style={{ marginBottom: 12 }}>
                        <text.T10 style={{ color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>Description</text.T10>
                        <text.T14 style={{ color: theme.colors.mainDark, lineHeight: 1.5, whiteSpace: 'pre-wrap' } as any}>
                          {task.description}
                        </text.T14>
                      </div>
                    )}

                    {task.assignee_names?.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <text.T10 style={{ color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>Assignee</text.T10>
                        <text.T14 style={{ color: theme.colors.mainDark }}>{task.assignee_names.join(', ')}</text.T14>
                      </div>
                    )}

                    {/* Feedback — only for completed */}
                    {task.status === 'completed' && task.feedback && (
                      <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 12, marginBottom: 12 }}>
                        <text.T10 style={{ color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Submission Report</text.T10>
                        {task.feedback.feedback_description && (
                          <div style={{ marginBottom: 10 }}>
                            <text.T14 style={{ color: theme.colors.mainDark, lineHeight: 1.5, whiteSpace: 'pre-wrap' } as any}>
                              {task.feedback.feedback_description}
                            </text.T14>
                          </div>
                        )}
                        {task.feedback.attachments?.length > 0 && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, marginBottom: 10 }}>
                            {task.feedback.attachments.map((att: any, aIdx: number) => (
                              <img key={aIdx} src={`${imageBaseUrl}${att.file_path}`} alt=""
                                onClick={() => setLightbox({ attachments: task.feedback.attachments, idx: aIdx })}
                                style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
                              />
                            ))}
                          </div>
                        )}
                        {task.feedback.reviewer_notes && (
                          <div style={{ padding: '8px 10px', borderRadius: 8, backgroundColor: '#F3F4F6' }}>
                            <text.T10 style={{ color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>Reviewer Notes</text.T10>
                            <text.T12 style={{ color: '#374151' }}>{task.feedback.reviewer_notes}</text.T12>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 12, marginTop: 4 }}>
                <text.T10 style={{ color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>All Time</text.T10>
                <div style={{ display: 'flex', gap: 20 }}>
                  <div>
                    <text.T10 style={{ color: '#9CA3AF' }}>Total</text.T10>
                    <text.T16 style={{ color: theme.colors.mainDark, fontWeight: '700' }}>{vesselSummary.total_tasks}</text.T16>
                  </div>
                  <div>
                    <text.T10 style={{ color: '#9CA3AF' }}>Completed</text.T10>
                    <text.T16 style={{ color: '#059669', fontWeight: '700' }}>{vesselSummary.total_completed}</text.T16>
                  </div>
                  {vesselSummary.open_tasks_count > 0 && (
                    <div>
                      <text.T10 style={{ color: '#9CA3AF' }}>Open</text.T10>
                      <text.T16 style={{ color: '#DC2626', fontWeight: '700' }}>{vesselSummary.open_tasks_count}</text.T16>
                    </div>
                  )}
                  {vesselSummary.avg_score != null && (
                    <div>
                      <text.T10 style={{ color: '#9CA3AF' }}>Avg Score</text.T10>
                      <text.T16 style={{ color: theme.colors.mainDark, fontWeight: '700' }}>{vesselSummary.avg_score}%</text.T16>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lightbox — TaskDetails style */}
        {lightbox && (() => {
          const att = lightbox.attachments[lightbox.idx];
          const url = `${imageBaseUrl}${att.file_path}`;
          const hasPrev = lightbox.idx > 0;
          const hasNext = lightbox.idx < lightbox.attachments.length - 1;
          return (
            <div
              onClick={() => setLightbox(null)}
              style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.75)',
                backdropFilter: 'blur(50px)',
                WebkitBackdropFilter: 'blur(20px)',
                zIndex: 10000,
                display: 'flex', flexDirection: 'column',
              }}
            >
              {/* Close button */}
              <div
                onClick={() => setLightbox(null)}
                style={{
                  position: 'absolute', top: 20, right: 20,
                  width: 44, height: 44, borderRadius: 22,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(5px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', zIndex: 10002,
                }}
              >
                <CloseIcon size={24} color="white" />
              </div>

              {/* Image area */}
              <div
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 20, overflow: 'hidden', position: 'relative',
                }}
              >
                {hasPrev && (
                  <div
                    onClick={e => { e.stopPropagation(); setLightbox(l => l && ({ ...l, idx: l.idx - 1 })); }}
                    style={{
                      position: 'absolute', left: 16,
                      width: 44, height: 44, borderRadius: 22,
                      backgroundColor: 'rgba(0,0,0,0.55)',
                      border: '1.5px solid rgba(255,255,255,0.4)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', zIndex: 10005,
                    }}
                  >
                    <ChevronLeftIcon size={32} color="white" />
                  </div>
                )}
                <img
                  src={url}
                  alt=""
                  onClick={e => e.stopPropagation()}
                  style={{
                    maxWidth: '100%', maxHeight: '100%',
                    objectFit: 'contain', borderRadius: 8,
                    boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                  }}
                />
                {hasNext && (
                  <div
                    onClick={e => { e.stopPropagation(); setLightbox(l => l && ({ ...l, idx: l.idx + 1 })); }}
                    style={{
                      position: 'absolute', right: 16,
                      width: 44, height: 44, borderRadius: 22,
                      backgroundColor: 'rgba(0,0,0,0.55)',
                      border: '1.5px solid rgba(255,255,255,0.4)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', zIndex: 10005,
                    }}
                  >
                    <ChevronRightIcon size={32} color="white" />
                  </div>
                )}
              </div>

              {/* Bottom panel */}
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  width: '100%',
                  backgroundColor: '#111827',
                  borderTop: '1px solid #374151',
                  borderTopLeftRadius: 20, borderTopRightRadius: 20,
                  boxShadow: '0 -4px 20px rgba(0,0,0,0.4)',
                  padding: '16px 20px 36px',
                  display: 'flex', flexDirection: 'column', gap: 12,
                }}
              >
                {att.note && (
                  <div style={{
                    backgroundColor: '#1F2937',
                    padding: '12px 14px', borderRadius: 12,
                    borderLeft: `4px solid ${theme.colors.mainColor}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: theme.colors.mainColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <text.T14 style={{ fontSize: 10, color: 'white', fontWeight: 'bold' }}>i</text.T14>
                      </div>
                      <text.T14 style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, fontWeight: '700', letterSpacing: 0.5 }}>NOTE</text.T14>
                    </div>
                    <text.T14 style={{ color: 'white', lineHeight: 1.5 }}>{att.note}</text.T14>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <text.T12 style={{ color: 'rgba(255,255,255,0.5)' }}>
                    {lightbox.idx + 1} / {lightbox.attachments.length}
                  </text.T12>
                </div>
              </div>
            </div>
          );
        })()}

        <div style={{marginBottom: 16}}>
          <text.H5 style={{color: theme.colors.mainDark, marginBottom: 10}}>{t.createTask.totalDuration}</text.H5>

          <div
            onClick={() => setIsDurationManual(!isDurationManual)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 10px', backgroundColor: isDurationManual ? '#F0FDF4' : '#FFF', borderRadius: 8,
              border: isDurationManual ? '1px solid #22C55E' : '1px solid #FFEFE6',
              cursor: 'pointer', marginBottom: 10
            }}
          >
            {isDurationManual ? <CheckBoxIcon size={20} color="#22C55E" /> : <CheckBoxOutlineBlankIcon size={20} color="#9CA3AF" />}
            <text.T14 style={{color: isDurationManual ? '#22C55E' : theme.colors.mainDark, fontWeight: isDurationManual ? '600' : 'normal'}}>
              {(t.createTask as any).manualDurationCheckbox || 'Input duration manually'}
            </text.T14>
          </div>

          {isDurationManual ? (
            <div style={{display: 'flex', gap: 10}}>
              <div style={{flex: 1}}>
                <text.T14 style={{color: '#6B7280', marginBottom: 6, fontSize: 12}}>{(t.createTask as any).startDate || 'Start Date'}</text.T14>
                <div style={{
                  padding: '14px 20px',
                  backgroundColor: theme.colors.white,
                  border: '1px solid #FFEFE6', borderRadius: 10,
                }}>
                  <input
                    type="datetime-local"
                    value={manualStartDate}
                    onChange={(e) => setManualStartDate(e.target.value)}
                    style={{
                      width: '100%', outline: 'none', border: 'none', fontSize: 16,
                      ...theme.fonts.SourceSansPro_400Regular,
                      color: manualStartDate ? 'inherit' : '#9CA3AF',
                    }}
                  />
                </div>
              </div>
              <div style={{flex: 1}}>
                <text.T14 style={{color: '#6B7280', marginBottom: 6, fontSize: 12}}>{(t.createTask as any).manualDurationLabel || 'Duration'}</text.T14>
                <div style={{
                  display: 'flex', gap: 8,
                  padding: '14px 20px',
                  backgroundColor: theme.colors.white,
                  border: '1px solid #FFEFE6', borderRadius: 10,
                  alignItems: 'center',
                }}>
                  <input
                    type="number"
                    min="0"
                    placeholder='0'
                    value={manualDurationHours}
                    onChange={(e) => setManualDurationHours(e.target.value)}
                    style={{
                      width: '100%', outline: 'none', border: 'none', fontSize: 16,
                      ...theme.fonts.SourceSansPro_400Regular,
                      textAlign: 'center',
                    }}
                  />
                  <text.T14 style={{color: '#9CA3AF', flexShrink: 0, fontSize: 12}}>h</text.T14>
                  <div style={{width: 1, height: 20, backgroundColor: '#E5E7EB', flexShrink: 0}} />
                  <input
                    type="number"
                    min="0"
                    max="59"
                    placeholder='0'
                    value={manualDurationMins}
                    onChange={(e) => setManualDurationMins(e.target.value)}
                    style={{
                      width: '100%', outline: 'none', border: 'none', fontSize: 16,
                      ...theme.fonts.SourceSansPro_400Regular,
                      textAlign: 'center',
                    }}
                  />
                  <text.T14 style={{color: '#9CA3AF', flexShrink: 0, fontSize: 12}}>m</text.T14>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '14px 20px', backgroundColor: '#EFF6FF', borderRadius: 10, border: '1px solid #BFDBFE' }}>
              <text.T16 style={{ color: '#1D4ED8', fontWeight: 'bold' }}>
                {totalDuration}
              </text.T16>
            </div>
          )}
        </div>

        {/* Attachment Selection Checkboxes */}
        {availableAttachments.length > 0 && (
          <div style={{marginBottom: 20}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
              <text.H5 style={{color: theme.colors.mainDark}}>{t.section?.requiredAttachments || 'Checklist Pekerjaan'}</text.H5>
              {filteredAttachments.length > 0 && filteredAttachments.every(att => selectedAttachments.includes(att.id)) ? (
                <div onClick={handleUncheckAll} style={{cursor: 'pointer'}}>
                  <text.T14 style={{color: '#9CA3AF', fontWeight: '600'}}>{t.createTask?.uncheckAll || 'Uncheck All'}</text.T14>
                </div>
              ) : (
                <div onClick={handleCheckAll} style={{cursor: 'pointer'}}>
                  <text.T14 style={{color: theme.colors.mainColor, fontWeight: '600'}}>{t.createTask?.checkAll || 'Check All'}</text.T14>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 10 }}>
              <custom.InputField
                placeholder={(t.createTask as any)?.searchChecklist || "Cari checklist..."}
                value={searchChecklist}
                onChange={(e: any) => setSearchChecklist(e.target.value)}
                leftIcon={<SearchIcon size={20} color="#9CA3AF" />}
                containerStyle={{ marginBottom: 0 }}
              />
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              maxHeight: 300,
              overflowY: 'auto',
              padding: 10,
              border: '1px solid #E5E7EB',
              borderRadius: 10,
              backgroundColor: '#F9FAFB'
            }}>
              {groupedAttachments.length > 0 ? groupedAttachments.map((group) => {
                const isGroupSelected = selectedGroups.includes(group.group_name);
                return (
                  <div key={group.group_name} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div
                      onClick={() => toggleGroupOpen(group.group_name)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px', backgroundColor: '#FFF', borderRadius: 8,
                        border: isGroupSelected ? `1px solid ${theme.colors.mainColor}` : '1px solid #E5E7EB',
                        cursor: 'pointer'
                      }}
                    >
                      <div onClick={(e) => toggleGroup(group.group_name, e)} style={{ flexShrink: 0 }}>
                        {isGroupSelected ?
                          <CheckBoxIcon size={24} color={theme.colors.mainColor} /> :
                          <CheckBoxOutlineBlankIcon size={24} color="#9CA3AF" />
                        }
                      </div>
                      <text.T16 style={{color: isGroupSelected ? theme.colors.mainDark : '#6B7280', flex: 1}}>{group.group_name}</text.T16>
                      <ArrowIcon size={20} color="#9CA3AF" style={{ transform: openGroups.includes(group.group_name) ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease-in-out' }} />
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateRows: openGroups.includes(group.group_name) ? '1fr' : '0fr',
                      transition: 'grid-template-rows 0.3s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out',
                      opacity: openGroups.includes(group.group_name) ? 1 : 0,
                      marginTop: openGroups.includes(group.group_name) ? 8 : 0,
                    }}>
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                        gap: 10,
                        paddingLeft: 10,
                        }}>
                          {group.attachments.map((att) => {
                            const isSelected = selectedAttachments.includes(att.id);
                            return (
                              <div
                                key={att.id}
                                onClick={() => toggleAttachment(att.id)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 10,
                                padding: '10px', backgroundColor: '#FFF', borderRadius: 8,
                                  border: isSelected ? `1px solid ${theme.colors.mainColor}` : '1px solid #E5E7EB',
                                  cursor: 'pointer'
                                }}
                              >
                                {isSelected ?
                                <CheckBoxIcon size={24} color={theme.colors.mainColor} /> :
                                <CheckBoxOutlineBlankIcon size={24} color="#9CA3AF" />
                                }
                              <text.T16 style={{color: isSelected ? theme.colors.mainDark : '#6B7280'}}>{att.title}</text.T16>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <text.T14 style={{color: '#9CA3AF', textAlign: 'center', marginTop: 10}}>{t.createTask?.noChecklistFound || 'Tidak ada checklist yang cocok.'}</text.T14>
              )}
            </div>
          </div>
        )}

        <div style={{marginBottom: 16}}>
          <text.H5 style={{color: theme.colors.mainDark, marginBottom: 10}}>{t.createTask.attachment}</text.H5>
          
          <div style={{display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 10}}>
            {attachments.map((file, index) => (
              <div key={index} style={{ position: 'relative', width: 100, height: 100, flexShrink: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                {file.type.startsWith('image/') ? (
                  <img src={URL.createObjectURL(file)} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', backgroundColor: '#F9FAFB', flexDirection: 'column' }}>
                    <AttachFileIcon size={24} color={theme.colors.mainColor} />
                    <text.T14 style={{fontSize: 10, marginTop: 4}} numberOfLines={1}>{file.name}</text.T14>
                  </div>
                )}
                <div onClick={() => removeAttachment(index)} style={{ position: 'absolute', top: 4, right: 4, cursor: 'pointer', padding: 4, backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '50%' }}>
                  <DeleteIcon size={16} color="#EF4444" />
                </div>
              </div>
            ))}
            
            <div 
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: 100, height: 100, flexShrink: 0,
                border: '1px dashed #9CA3AF', borderRadius: 10, 
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                backgroundColor: '#F9FAFB', cursor: 'pointer'
              }}
            >
              <AttachFileIcon size={24} color="#9CA3AF" />
              <text.T14 style={{marginTop: 4, color: '#6B7280', fontSize: 12, textAlign: 'center'}}>{t.createTask.tapToUpload || 'Add'}</text.T14>
            </div>
          </div>

          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            style={{display: 'none'}} 
            accept="image/*,.pdf,.doc,.docx"
            multiple
          />
        </div>

        <div
          style={{
            width: '100%',
            padding: '14px 20px',
            backgroundColor: theme.colors.white,
            border: '1px solid #FFEFE6',
            borderRadius: 10,
            marginBottom: 16,
          }}
        >
          <textarea
            placeholder={t.createTask.descriptionPlaceholder}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{
              width: '100%',
              height: 120,
              ...theme.fonts.SourceSansPro_400Regular,
              outline: 'none',
              border: 'none',
              resize: 'none',
              whiteSpace: 'pre-wrap'
            }}
          />
        </div>
      </main>
    );
  };

  const renderButton = (): JSX.Element => {
    return (
      <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          backgroundColor: 'white', padding: '16px 24px 32px',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.05)',
          zIndex: 10
      }}>
          <div className='container' style={{padding: 0, maxWidth: 650, margin: '0 auto'}}>
              {/* {buttons} */}
              <components.Button
                title={loading ? t.common.saving : t.createTask.button}
                onClick={handleCreateTask}
              />
          </div>
      </div>
    );
  };

  const renderAlert = () => {
    if (!alertConfig.isOpen) return null;
    return (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000,
          display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}
      >
        <div
          style={{
            backgroundColor: 'white', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 320, textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column', alignItems: 'center'
          }}
        >
          <div style={{marginBottom: 16}}>
            {alertConfig.type === 'success' && <CheckCircleIcon size={60} color="#10B981" />}
            {alertConfig.type === 'error' && <ErrorIcon size={60} color="#EF4444" />}
            {alertConfig.type === 'warning' && <WarningIcon size={60} color="#F59E0B" />}
          </div>
          <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
          <text.T16 style={{ marginBottom: 24, color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>
            {alertConfig.message}
          </text.T16>
          <components.Button title={t.common.ok} onClick={hideAlert} containerStyle={{width: '100%'}} />
        </div>
      </div>
    );
  };

  return (
    <div id='screen'>
      {renderBackground()}
      {renderHeader()}
      <div style={{padding: 0}}>
        {renderContent()}
      </div>
      {renderButton()}
      {renderAlert()}
    </div>
  );
};
